import React, { createContext, useContext, useEffect, useState } from 'react';
import type { ThemeType } from '@/themes/types';
import { themes } from '@/themes/types';

interface ThemeContextType {
    theme: ThemeType;
    setTheme: (theme: ThemeType) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
    const [theme, setThemeState] = useState<ThemeType>('global');

    const setTheme = (newTheme: ThemeType) => {
        setThemeState(newTheme);
    };

    useEffect(() => {
        const root = window.document.documentElement;
        const config = themes[theme] || themes.global;

        // Apply colors as CSS variables
        root.style.setProperty('--primary', config.primary);
        root.style.setProperty('--foreground', config.foreground);
        root.style.setProperty('--accent', config.accent);
        root.style.setProperty('--background', config.background);
        root.style.setProperty('--card', config.card);
        root.style.setProperty('--card-foreground', config.cardForeground);
        root.style.setProperty('--border', config.border);
        root.style.setProperty('--muted', config.muted);
        root.style.setProperty('--muted-foreground', config.mutedForeground);

        // Semantic Status Colors
        if (config.success) root.style.setProperty('--success', config.success);
        if (config.warning) root.style.setProperty('--warning', config.warning);
        if (config.error) root.style.setProperty('--error', config.error);
        if (config.info) root.style.setProperty('--info', config.info);

        // Hero Gradient Variables
        if (config.heroGradientFrom) root.style.setProperty('--hero-gradient-from', config.heroGradientFrom);
        if (config.heroGradientTo) root.style.setProperty('--hero-gradient-to', config.heroGradientTo);

        // Sidebar variables
        root.style.setProperty('--sidebar-background', config.sidebarBackground);
        root.style.setProperty('--sidebar-primary', config.sidebarPrimary);

        root.setAttribute('data-theme', theme);
    }, [theme]);

    return (
        <ThemeContext.Provider value={{ theme, setTheme }}>
            {children}
        </ThemeContext.Provider>
    );
}

export const useProductTheme = () => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useProductTheme must be used within a ThemeProvider');
    }
    return context;
};
